import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis
} from "recharts";

import { EventQuery, mapQueryToString } from "lib/build/events";

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from "src/common/components/Card";
import { ChartTooltip, ChartWrapper } from "src/common/components/Chart";
import {
	ChartTooltipLabel,
	ChartTooltipValue
} from "src/common/components/Chart/ChartTooltip";
import { FullCardPreloader } from "src/common/components/Preloader";
import { fetchWithRetries } from "src/common/utils/api";
import { useCreateGenericError } from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { findEvent, RawPatient } from "src/modules/shared/patient-list";

const messages = defineMessages({
	title: {
		id: "charts.status.title",
		defaultMessage: "Páciens státuszok eloszlása"
	},
	status: {
		id: "charts.status.status",
		defaultMessage: "Státusz"
	},
	count: {
		id: "chart.status.count",
		defaultMessage: "Páciensek száma"
	}
});

interface StatusChartProps {
	statusQuery: EventQuery;
	formatLongStatus(status: string): string;
	formatShortStatus(status: string): string;
}

export const StatusChart: React.FunctionComponent<StatusChartProps> = ({
	statusQuery,
	formatLongStatus,
	formatShortStatus
}) => {
	const { org, mod } = useOrgModParams();

	const [isLoading, setLoading] = React.useState(false);
	const [values, setValues] = React.useState<string[]>([]);

	const createGenericError = useCreateGenericError();

	const fetchPatients = React.useCallback(async () => {
		setLoading(true);
		setValues([]);
		try {
			const params: string[] = [];
			params.push(`organization=${org}`);
			params.push(`module=${mod}`);
			params.push(`i=${mapQueryToString(statusQuery)}`);
			const url = `/patients?${params.join("&")}`;
			const { data } = await fetchWithRetries<RawPatient[]>(url, 0);
			setLoading(false);
			setValues(
				data.map(patient => {
					const event = findEvent<string>(patient.extra, statusQuery);
					return event === null ? "" : event.value;
				})
			);
		} catch {
			setLoading(false);
			createGenericError();
		}
	}, [org, mod, createGenericError, statusQuery]);

	React.useEffect(() => {
		fetchPatients();
	}, [fetchPatients]);

	const data = React.useMemo(() => {
		const map: { [key: string]: number } = {};
		for (const value of values) {
			if (value in map) {
				map[value]++;
			} else {
				map[value] = 1;
			}
		}
		return Object.entries(map)
			.map(([key, value]) => ({
				longName: formatLongStatus(key),
				shortName: formatShortStatus(key),
				count: value
			}))
			.sort((a, b) => {
				if (a.count < b.count) {
					return 1;
				}
				if (a.count > b.count) {
					return -1;
				}
				return 0;
			});
	}, [values, formatLongStatus, formatShortStatus]);

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					<FormattedMessage {...messages.title} />
				</CardTitle>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<FullCardPreloader />
				) : (
					<ChartWrapper>
						<ResponsiveContainer height={200}>
							<BarChart data={data}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="shortName" />
								<YAxis />
								<Tooltip
									wrapperStyle={{ outline: "none" }}
									content={<CustomTooltip />}
								/>
								<Bar
									dataKey="count"
									fill="var(--color-record)"
								/>
							</BarChart>
						</ResponsiveContainer>
					</ChartWrapper>
				)}
			</CardContent>
		</Card>
	);
};

interface CustomTooltipProps {
	active?: boolean;
	payload?: {
		value: string;
		fill: string;
		payload: {
			longName: string;
		};
	}[];
}

const CustomTooltip: React.FunctionComponent<CustomTooltipProps> = ({
	active,
	payload
}) => {
	if (active === true && payload !== undefined && payload.length) {
		return (
			<ChartTooltip>
				<ChartTooltipLabel>
					{payload[0].payload.longName}
				</ChartTooltipLabel>
				<ChartTooltipValue style={{ color: payload[0].fill }}>
					<FormattedMessage {...messages.count} />: {payload[0].value}
				</ChartTooltipValue>
			</ChartTooltip>
		);
	}
	return null;
};
